import { Control, FieldValues, Path } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

type CustomRadioGroupProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  disabled?: boolean;
  options: { value: string; label: string; isItemDisabled?: boolean; tooltip?: string }[];
};

export function CustomRadioGroup<T extends FieldValues>({
  name,
  control,
  label = 'Select an option',
  disabled = false,
  options,
}: CustomRadioGroupProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          {label && <FormLabel htmlFor={name}>{label}</FormLabel>}
          <FormControl>
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="flex flex-wrap gap-2"
              disabled={disabled}
            >
              {options.map(({ value, label, isItemDisabled = false, tooltip }) => {
                const optionLabel = (
                  <label
                    htmlFor={`${name}-${value}`}
                    aria-label={label}
                    className={cn(
                      'flex min-w-24 flex-1 items-center justify-center rounded-full px-4 py-2 text-sm transition-colors',
                      isItemDisabled
                        ? 'cursor-default bg-muted/50 text-muted-foreground'
                        : 'cursor-pointer bg-muted hover:bg-muted/80',
                      !isItemDisabled &&
                        'peer-data-[state=checked]:bg-foreground peer-data-[state=checked]:text-background',
                      disabled ? 'opacity-50 cursor-default' : 'opacity-100 cursor-pointer'
                    )}
                  >
                    <span className="truncate whitespace-nowrap">{label}</span>
                  </label>
                );

                return (
                  <div key={value} className="relative">
                    <RadioGroupItem
                      value={value}
                      id={`${name}-${value}`}
                      className="peer sr-only"
                      disabled={isItemDisabled}
                    />
                    {tooltip ? (
                      <Tooltip>
                        <TooltipTrigger asChild>{optionLabel}</TooltipTrigger>
                        <TooltipPortal>
                          <TooltipContent className="max-w-xs whitespace-pre-line text-pretty">
                            {tooltip}
                          </TooltipContent>
                        </TooltipPortal>
                      </Tooltip>
                    ) : (
                      optionLabel
                    )}
                  </div>
                );
              })}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
